import React from "react";
import { useTranslation } from "react-i18next";

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  hasNextPage,
  hasPrevPage,
  totalCount,
  pageSize,
}) => {
  const { t } = useTranslation();

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // If total pages is less than or equal to maxPagesToShow, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always include first page
      pages.push(1);

      // Calculate start and end of page range to show
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if at the beginning
      if (currentPage <= 2) {
        endPage = Math.min(4, totalPages - 1);
      }

      // Adjust if at the end
      if (currentPage >= totalPages - 1) {
        startPage = Math.max(2, totalPages - 3);
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always include last page if more than 1 page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex flex-col items-center space-y-2 py-4">
      <div className="text-sm text-gray-500">
        {t("pagination.showing")} {(currentPage - 1) * pageSize + 1}-
        {Math.min(currentPage * pageSize, totalCount)} {t("pagination.of")}{" "}
        {totalCount}
      </div>

      <div className="flex space-x-1">
        {/* Previous button */}
        <button
          onClick={() => hasPrevPage && onPageChange(currentPage - 1)}
          disabled={!hasPrevPage}
          className={`px-3 py-1 rounded-md ${
            hasPrevPage
              ? "bg-white text-gray-700 border hover:bg-gray-50"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
          aria-label={t("pagination.previous")}
        >
          &laquo;
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            onClick={() => typeof page === "number" && onPageChange(page)}
            disabled={typeof page !== "number"}
            className={`px-3 py-1 rounded-md ${
              page === currentPage
                ? "bg-primary text-white"
                : typeof page === "number"
                ? "bg-white text-gray-700 border hover:bg-gray-50"
                : "bg-white text-gray-700 cursor-default"
            }`}
          >
            {page}
          </button>
        ))}

        {/* Next button */}
        <button
          onClick={() => hasNextPage && onPageChange(currentPage + 1)}
          disabled={!hasNextPage}
          className={`px-3 py-1 rounded-md ${
            hasNextPage
              ? "bg-white text-gray-700 border hover:bg-gray-50"
              : "bg-gray-100 text-gray-400 cursor-not-allowed"
          }`}
          aria-label={t("pagination.next")}
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

export default Pagination;
